import { useFormik } from "formik";
import { Link } from "react-router-dom";
import { SelectList, Table } from "../../../../../../components/Functional";
import { Button, getInput, Modal } from "../../../../../../components/ui";
import { Checkbox } from "../../../../../../components/ui/Popovers";
import { LIST_TYPES } from "../../../../../../constants/listTypes";
import { UPDATE_OFFER as updateOfferMutation } from "../../../../../../graphql/mutation/Offer";
import { FETCH_OPTIMISED_OFFERS as fetchOffers } from "../../../../../../graphql/query/Offer";
import useOfferStatus from "../../../../../../hooks/useOfferStatus";
import useUser from "../../../../../../hooks/useUser";
import EditOffer from "../../../../../Offer/pages/List/components/EditOffer";
import SectionHeader from "../../components/SectionHeader";
import Actions from "./components/Actions";
import MoveOfferModal from "./components/MoveOfferModal";
import RefuseOfferModal from "./components/RefuseOfferModal";

const ContactOffers = (props = {}) => {
  const { contact } = props;
  const { hasPermission } = useUser();
  const { findOfferStatusById } = useOfferStatus();
  const formik = useFormik({
    initialValues: {},
  });

  const toggleProps = props => {
    const { id } = props || {};
    const name = `moveOffers.ids._${id}`;

    return {
      designType: "checkbox",
      type: "toggle",
      name,
      ...formik.getFieldProps(name),
      onChange: value =>
        formik.getFieldHelpers(name).setValue(value ? true : undefined),
    };
  };

  const SelectAll = props => {
    const { rows } = props || {};

    const checkboxProps = {
      formik,
      label: "Offers",
      name: "moveOffers",
      rows,
    };

    return <Checkbox {...checkboxProps} />;
  };

  const tableProps = {
    variant: "dynamicHeight",
    dataKey: "offers",
    headers: [
      {
        label: SelectAll,
        type: "custom",
        component: props => getInput(toggleProps(props)),
      },
      {
        label: "Date",
        name: "date",
        type: "date",
      },
      { label: "Image", name: "artwork.imagesSummary.imgT", type: "image" },
      {
        label: "Artist",
        name: "artists",
        type: "custom",
        component: props => (
          <div
            className={`overflow-hidden text-ellipsis text-base font-medium leading-5 text-black`}
            style={{ maxWidth: 150 }}
          >
            {(
              props?.values?.artwork?.artists?.map(item => item?.name) || []
            )?.join(", ")}
          </div>
        ),
      },
      {
        label: "Artwork Title",
        name: "artwork.title",
        type: "custom",
        component: props => {
          const title = props?.values?.artwork?.title;

          return (
            <Link
              className="block w-[200px] overflow-hidden text-ellipsis whitespace-nowrap underline"
              to={`/artworks/${props?.values?.artwork?.id}/details`}
              title={title}
            >
              {title}
            </Link>
          );
        },
      },
      {
        label: "Status",
        name: "statusId",
        sortKey: "statusId",
        type: "custom",
        component: props => {
          return findOfferStatusById(props?.values?.statusId)?.description;
        },
      },
      {
        label: "Currency",
        name: "currency.code",
        sortKey: "currencyId",
        type: "label",
      },
      { label: "Amount", name: "amount", sortKey: "amount", type: "currency" },
      {
        label: "Top Deal",
        name: "priority",
        type: "input",
        inputType: "checkbox",
        singleCheckbox: true,
        options: [{ value: -1 }],
      },
      {
        label: "Hold",
        name: "hold.name",
        type: "custom",
        component: props => {
          return (
            <Link
              className="underline"
              to={`/instance/${props?.values?.hold?.id}/details`}
            >
              {props?.values?.hold?.name}
            </Link>
          );
        },
      },
      {
        label: "Closed",
        name: "dateClosed",
        sortKey: "dateClosed",
        type: "date",
      },
      { label: "User", name: "user", sortKey: "user", type: "label" },
      {
        label: "Notes",
        name: "notes",
        sortKey: "notes",
        type: "label",
      },
      { label: "", type: "custom", component: Actions },
    ],
    FETCH_QUERY: fetchOffers(),
    variables: {
      contactId: contact?.id,
      order: ["date", "DESC"],
    },
    UPDATE_MUTATION: updateOfferMutation(),
    isSubTable: true,
  };

  const disabled =
    Object.keys(formik?.values?.moveOffers?.ids || {})?.length === 0 ||
    !hasPermission("MOVE_OFFER");
  const modalProps = {
    body: MoveOfferModal,
    closeOnBackdrop: false,
    contact,
    disabled,
    hideCloseButton: true,
    scale: "md",
    values: formik?.values,
    onClose: () => {
      formik?.resetForm();
    },
  };

  const createListProps = {
    ids: Object.keys(formik?.values?.moveOffers?.ids || {})?.map(item =>
      parseInt(item.replace("_", "")),
    ),
    onSuccess: () => formik?.resetForm(),
    typeId: LIST_TYPES.OFFER_WORKING,
  };

  const offersActions = [
    {
      component: () => {
        const modalProps = {
          body: EditOffer,
          closeOnBackdrop: false,
          offer: { contact },
          hideCloseButton: false,
          scale: "lg",
        };

        return (
          <Modal {...modalProps}>
            <Button label="Add Offer" action="black" />
          </Modal>
        );
      },
    },
    {
      component: () => <SelectList.CreateList {...createListProps} />,
    },
    {
      component: () => {
        return (
          <Modal {...{ ...modalProps, body: RefuseOfferModal }}>
            <Button
              label="Refuse All Offers"
              action="black"
              disabled={disabled}
            />
          </Modal>
        );
      },
    },
    {
      component: () => {
        return (
          <Modal {...modalProps}>
            <Button label="Move Offer" action="black" disabled={disabled} />
          </Modal>
        );
      },
    },
  ];

  return (
    <div className="w-full">
      <SectionHeader title="Offers" actions={offersActions} />
      <div className="mb-6 w-full">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export default ContactOffers;
